import dayjs, { Dayjs } from 'dayjs'
import {
  ATTESTOR_DEFAULTS,
  DEFAULT_COUNTRY,
  PROGRAM_MANAGER_DEFAULTS,
} from 'src/constants/payments-ops/bankOnboardingDefaults'
import { MASKED_VALUE } from 'src/constants/payments-ops/constants'
import {
  BankOnboardingAddressType,
  BankOnboardingStatus,
  EntityIdentificationType,
  LegalStructure,
  PersonIdentificationType,
} from 'src/constants/payments-ops/enums'
import type {
  BankOnboarding,
  BankOnboardingAddress,
  BankOnboardingFormValues,
  BeneficialOwnerFormValues,
  StartBankOnboardingBody,
} from 'src/interfaces/bankOnboarding'

const DATE_FORMAT = 'YYYY-MM-DD'

const digitsOnly = (value: string): string => {
  return value.replace(/\D/g, '')
}

/** The list masks SSN/EIN/DOB/ID-type values as '******' at every level — never pre-fill those. */
const unmask = (value: unknown): string => {
  if (value === null || value === undefined || value === MASKED_VALUE) {
    return ''
  }

  return String(value)
}

const unmaskDate = (value: unknown): Dayjs | null => {
  const text = unmask(value)

  if (text === '') {
    return null
  }

  const parsed = dayjs(text)

  return parsed.isValid() ? parsed : null
}

const unmaskEnum = <T extends string>(value: unknown, allowed: T[]): T | '' => {
  const text = unmask(value)

  return allowed.includes(text as T) ? (text as T) : ''
}

const buildAddress = (
  addressType: BankOnboardingAddressType,
  fields: {
    addressLine1: string
    addressLine2: string
    city: string
    state: string
    zipCode: string
    country: string
  },
): BankOnboardingAddress => {
  const address: BankOnboardingAddress = {
    addressType,
    addressLine1: fields.addressLine1.trim(),
    city: fields.city.trim(),
    state: fields.state.trim().toUpperCase(),
    zipCode: fields.zipCode.trim(),
    country: fields.country.trim().toUpperCase(),
  }

  // Strict DTO: an empty optional is omitted rather than sent as ''.
  if (fields.addressLine2.trim() !== '') {
    address.addressLine2 = fields.addressLine2.trim()
  }

  return address
}

export const buildEmptyBeneficialOwner = (): BeneficialOwnerFormValues => {
  return {
    firstName: '',
    lastName: '',
    ownershipPercentage: '',
    identificationType: '',
    identificationNumber: '',
    identificationIssuingCountry: DEFAULT_COUNTRY,
    dateOfBirth: null,
    phoneNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: DEFAULT_COUNTRY,
  }
}

export const buildEmptyOnboardingFormValues = (today: Dayjs): BankOnboardingFormValues => {
  return {
    partnerId: '',
    virtualAccountNickName: '',
    ...PROGRAM_MANAGER_DEFAULTS,
    legalName: '',
    entityIdentificationType: '',
    entityIdentificationNumber: '',
    legalStructure: '',
    hasAnyDba: false,
    dbaName: '',
    publiclyTradedStockSymbol: '',
    tradedExchange: '',
    naicsCode: '',
    natureOfBusiness: '',
    projectedAnnualRevenue: '',
    businessPhoneNumber: '',
    countryOfFormation: DEFAULT_COUNTRY,
    countryOfPrimaryBusinessOperations: DEFAULT_COUNTRY,
    msbServices: false,
    cipExempt: false,
    hasAnyBeneficialOwner: false,
    beneficialOwnerExempt: false,
    // Retool locked this on (`ui:disabled` + checked) — required for every onboarding.
    programParticipantAttestation: true,
    businessAddressLine1: '',
    businessAddressLine2: '',
    businessCity: '',
    businessState: '',
    businessZipCode: '',
    businessCountry: DEFAULT_COUNTRY,
    beneficialOwners: [],
    controlPersonTitle: '',
    controlPersonFirstName: '',
    controlPersonLastName: '',
    controlPersonIdentificationType: '',
    controlPersonIdentificationNumber: '',
    controlPersonIssuingCountry: DEFAULT_COUNTRY,
    controlPersonDateOfBirth: null,
    controlPersonPhoneNumber: '',
    controlPersonAddressLine1: '',
    controlPersonAddressLine2: '',
    controlPersonCity: '',
    controlPersonState: '',
    controlPersonZipCode: '',
    controlPersonCountry: DEFAULT_COUNTRY,
    ...ATTESTOR_DEFAULTS,
    dateOfAttestation: today,
  }
}

const PERSON_ID_TYPES = Object.values(PersonIdentificationType)

export const buildOnboardingFormValuesFromPayload = (
  payload: StartBankOnboardingBody,
  today: Dayjs,
): BankOnboardingFormValues => {
  const empty = buildEmptyOnboardingFormValues(today)
  const participant = payload.programParticipantDetails
  const additional = participant?.additionalDetails
  const controlPerson = participant?.controlPersonDetails
  const businessAddress = additional?.address
  const controlAddress = controlPerson?.controlPersonAddress

  return {
    ...empty,
    partnerId: unmask(payload.partnerId),
    virtualAccountNickName: unmask(payload.virtualAccountInfo?.details?.[0]?.nickName),
    programManagerIdentificationNumber: unmask(payload.programManagerDetails?.identificationNumber),
    programManagerName: unmask(payload.programManagerDetails?.programManagerName),
    contactName: unmask(payload.programManagerDetails?.contactName),
    contactEmailAddress: unmask(payload.programManagerDetails?.contactEmailAddress),
    legalName: unmask(participant?.entityDetails?.legalName),
    entityIdentificationType: unmaskEnum(
      participant?.entityDetails?.identificationType,
      Object.values(EntityIdentificationType),
    ),
    entityIdentificationNumber: unmask(participant?.entityDetails?.identificationNumber),
    legalStructure: unmaskEnum(additional?.legalStructure, Object.values(LegalStructure)),
    hasAnyDba: Boolean(additional?.dbaDetails?.hasAnyDBA),
    dbaName: unmask(additional?.dbaDetails?.dbaName),
    publiclyTradedStockSymbol: unmask(additional?.tradingDetails?.publiclyTradedStockSymbol),
    tradedExchange: unmask(additional?.tradingDetails?.tradedExchange),
    naicsCode: unmask(additional?.standardIndustryClassificationCode),
    natureOfBusiness: unmask(additional?.natureOfBusiness),
    projectedAnnualRevenue: unmask(additional?.projectedAnnualRevenue),
    businessPhoneNumber: unmask(additional?.businessPhoneNumber),
    countryOfFormation: unmask(additional?.countryOfFormation) || DEFAULT_COUNTRY,
    countryOfPrimaryBusinessOperations:
      unmask(additional?.countryOfPrimaryBusinessOperations) || DEFAULT_COUNTRY,
    msbServices: Boolean(additional?.msbServices),
    cipExempt: Boolean(additional?.cipExempt),
    hasAnyBeneficialOwner: Boolean(additional?.hasAnyBeneficialOwner),
    beneficialOwnerExempt: Boolean(additional?.beneficialOwnerExempt),
    businessAddressLine1: unmask(businessAddress?.addressLine1),
    businessAddressLine2: unmask(businessAddress?.addressLine2),
    businessCity: unmask(businessAddress?.city),
    businessState: unmask(businessAddress?.state),
    businessZipCode: unmask(businessAddress?.zipCode),
    businessCountry: unmask(businessAddress?.country) || DEFAULT_COUNTRY,
    beneficialOwners: (participant?.beneficialOwnershipDetails ?? []).map((owner) => {
      return {
        firstName: unmask(owner.beneficialOwnerFirstName),
        lastName: unmask(owner.beneficialOwnerLastName),
        ownershipPercentage: unmask(owner.ownershipPercentage),
        identificationType: unmaskEnum(owner.identificationType, PERSON_ID_TYPES),
        identificationNumber: unmask(owner.beneficialOwnerIdentificationNumber),
        identificationIssuingCountry: unmask(owner.identificationIssuingCountry) || DEFAULT_COUNTRY,
        dateOfBirth: unmaskDate(owner.beneficialOwnerDateOfBirth),
        phoneNumber: unmask(owner.beneficialOwnerPhoneNumber),
        addressLine1: unmask(owner.beneficialOwnerAddress?.addressLine1),
        addressLine2: unmask(owner.beneficialOwnerAddress?.addressLine2),
        city: unmask(owner.beneficialOwnerAddress?.city),
        state: unmask(owner.beneficialOwnerAddress?.state),
        zipCode: unmask(owner.beneficialOwnerAddress?.zipCode),
        country: unmask(owner.beneficialOwnerAddress?.country) || DEFAULT_COUNTRY,
      }
    }),
    controlPersonTitle: unmask(controlPerson?.titleOfControlPerson),
    controlPersonFirstName: unmask(controlPerson?.firstName),
    controlPersonLastName: unmask(controlPerson?.lastName),
    controlPersonIdentificationType: unmaskEnum(controlPerson?.identificationType, PERSON_ID_TYPES),
    controlPersonIdentificationNumber: unmask(controlPerson?.identificationNumber),
    controlPersonIssuingCountry:
      unmask(controlPerson?.identificationIssuingCountry) || DEFAULT_COUNTRY,
    controlPersonDateOfBirth: unmaskDate(controlPerson?.controlPersonDateOfBirth),
    controlPersonPhoneNumber: unmask(controlPerson?.controlPersonPhoneNumber),
    controlPersonAddressLine1: unmask(controlAddress?.addressLine1),
    controlPersonAddressLine2: unmask(controlAddress?.addressLine2),
    controlPersonCity: unmask(controlAddress?.city),
    controlPersonState: unmask(controlAddress?.state),
    controlPersonZipCode: unmask(controlAddress?.zipCode),
    controlPersonCountry: unmask(controlAddress?.country) || DEFAULT_COUNTRY,
    attestorFirstName: unmask(participant?.attestorDetails?.firstName) || empty.attestorFirstName,
    attestorLastName: unmask(participant?.attestorDetails?.lastName) || empty.attestorLastName,
    attestorJobTitle: unmask(participant?.attestorDetails?.jobTitle) || empty.attestorJobTitle,
    // A new attestation is attested today — the previous date is never reused.
    dateOfAttestation: today,
  }
}

/** Digits only, no leading zeros ('007' -> '7'); the validator already requires a value > 0. */
const normalizeRevenue = (value: string): string => {
  return value.replace(/[,\s$]/g, '').replace(/^0+(?=\d)/, '')
}

const formatDate = (value: Dayjs | null): string => {
  return value ? value.format(DATE_FORMAT) : ''
}

/**
 * Validated values → the strict nested DTO. Called only after every step validator passed, so
 * required fields are present; the casts from `'' | Enum` are safe at that point.
 */
export const buildStartBankOnboardingBody = (
  values: BankOnboardingFormValues,
): StartBankOnboardingBody => {
  const tradingDetails: StartBankOnboardingBody['programParticipantDetails']['additionalDetails']['tradingDetails'] =
    {}

  if (values.publiclyTradedStockSymbol.trim() !== '') {
    tradingDetails.publiclyTradedStockSymbol = values.publiclyTradedStockSymbol.trim()
  }

  if (values.tradedExchange.trim() !== '') {
    tradingDetails.tradedExchange = values.tradedExchange.trim()
  }

  const beneficialOwners = values.hasAnyBeneficialOwner ? values.beneficialOwners : []

  return {
    partnerId: values.partnerId,
    programManagerDetails: {
      identificationNumber: digitsOnly(values.programManagerIdentificationNumber),
      programManagerName: values.programManagerName.trim(),
      contactName: values.contactName.trim(),
      contactEmailAddress: values.contactEmailAddress.trim(),
    },
    virtualAccountInfo: {
      numberOfVirtualAccounts: 1,
      details: [{ nickName: values.virtualAccountNickName.trim() }],
    },
    programParticipantDetails: {
      entityDetails: {
        legalName: values.legalName.trim(),
        identificationNumber: digitsOnly(values.entityIdentificationNumber),
        identificationType: values.entityIdentificationType as EntityIdentificationType,
        programParticipantAttestation: values.programParticipantAttestation,
      },
      additionalDetails: {
        dbaDetails: values.hasAnyDba
          ? { hasAnyDBA: true, dbaName: values.dbaName.trim() }
          : { hasAnyDBA: false },
        tradingDetails,
        cipExempt: values.cipExempt,
        hasAnyBeneficialOwner: values.hasAnyBeneficialOwner,
        beneficialOwnerExempt: values.beneficialOwnerExempt,
        standardIndustryClassificationCode: values.naicsCode.trim(),
        natureOfBusiness: values.natureOfBusiness.trim(),
        legalStructure: values.legalStructure as LegalStructure,
        countryOfFormation: values.countryOfFormation.trim().toUpperCase(),
        countryOfPrimaryBusinessOperations: values.countryOfPrimaryBusinessOperations
          .trim()
          .toUpperCase(),
        projectedAnnualRevenue: normalizeRevenue(values.projectedAnnualRevenue),
        msbServices: values.msbServices,
        businessPhoneNumber: Number(digitsOnly(values.businessPhoneNumber)),
        address: buildAddress(BankOnboardingAddressType.Business, {
          addressLine1: values.businessAddressLine1,
          addressLine2: values.businessAddressLine2,
          city: values.businessCity,
          state: values.businessState,
          zipCode: values.businessZipCode,
          country: values.businessCountry,
        }),
      },
      beneficialOwnershipDetails: beneficialOwners.map((owner) => {
        return {
          beneficialOwnerFirstName: owner.firstName.trim(),
          beneficialOwnerLastName: owner.lastName.trim(),
          ownershipPercentage: Number(owner.ownershipPercentage),
          identificationIssuingCountry: owner.identificationIssuingCountry.trim().toUpperCase(),
          beneficialOwnerAddress: buildAddress(BankOnboardingAddressType.Residential, owner),
          beneficialOwnerIdentificationNumber: digitsOnly(owner.identificationNumber),
          identificationType: owner.identificationType as PersonIdentificationType,
          beneficialOwnerDateOfBirth: formatDate(owner.dateOfBirth),
          beneficialOwnerPhoneNumber: Number(digitsOnly(owner.phoneNumber)),
        }
      }),
      controlPersonDetails: {
        titleOfControlPerson: values.controlPersonTitle.trim(),
        firstName: values.controlPersonFirstName.trim(),
        lastName: values.controlPersonLastName.trim(),
        controlPersonAddress: buildAddress(BankOnboardingAddressType.Residential, {
          addressLine1: values.controlPersonAddressLine1,
          addressLine2: values.controlPersonAddressLine2,
          city: values.controlPersonCity,
          state: values.controlPersonState,
          zipCode: values.controlPersonZipCode,
          country: values.controlPersonCountry,
        }),
        controlPersonPhoneNumber: Number(digitsOnly(values.controlPersonPhoneNumber)),
        controlPersonDateOfBirth: formatDate(values.controlPersonDateOfBirth),
        identificationNumber: digitsOnly(values.controlPersonIdentificationNumber),
        identificationType: values.controlPersonIdentificationType as PersonIdentificationType,
        identificationIssuingCountry: values.controlPersonIssuingCountry.trim().toUpperCase(),
      },
      attestorDetails: {
        firstName: values.attestorFirstName.trim(),
        lastName: values.attestorLastName.trim(),
        jobTitle: values.attestorJobTitle.trim(),
        dateOfAttestation: formatDate(values.dateOfAttestation),
      },
    },
  }
}

const IN_PROGRESS_STATUSES = [
  BankOnboardingStatus.OnboardingStarted,
  BankOnboardingStatus.DataValidationInProcess,
  BankOnboardingStatus.CustomerRecordInProcess,
  BankOnboardingStatus.VirtualAccountSetupInProcess,
]

/** The backend 400s a new onboarding while one is in progress or completed for the partner. */
export const buildBlockedPartnerReasons = (
  onboardings: BankOnboarding[],
): Record<string, string> => {
  return onboardings.reduce<Record<string, string>>((reasons, onboarding) => {
    if (IN_PROGRESS_STATUSES.includes(onboarding.status)) {
      return { ...reasons, [onboarding.partnerId]: 'onboarding in progress' }
    }

    if (onboarding.status === BankOnboardingStatus.OnboardingProcessCompleted) {
      return { ...reasons, [onboarding.partnerId]: 'already onboarded' }
    }

    return reasons
  }, {})
}

/** For the review step and the confirm copy — never echo a full SSN/EIN back. */
export const maskIdentifier = (value: string): string => {
  const digits = digitsOnly(value)

  if (digits === '') {
    return '—'
  }

  return `•••••${digits.slice(-4)}`
}
